import React, { useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FiCheck, FiEye, FiEyeOff, FiX } from 'react-icons/fi';
import { authApi } from '../../../api/services/auth.service';
import { getMe, selectUser, updatePassword } from '../../../entities/auth/model';
import {
  setErrorNotification,
  setNotification,
} from '../../../app/store/notifications';
import { AppDispatch } from '../../../app/store/store';

const SPECIAL_CHARS = /[!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]/;

interface PasswordCriterion {
  label: string;
  hint: string;
  test: (v: string) => boolean;
}

const PASSWORD_CRITERIA: PasswordCriterion[] = [
  {
    label: 'Не короче 6 символов',
    hint: 'Чем длиннее – тем надежнее',
    test: (v) => v.length >= 6,
  },
  {
    label: '1 специальный символ',
    hint: 'Это все символы на клавиатуре, которые не являются буквами и цифрами.',
    test: (v) => SPECIAL_CHARS.test(v),
  },
];

const PasswordInput: React.FC<{
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  hasError?: boolean;
  hasSuccess?: boolean;
  helper?: React.ReactNode;
}> = ({ id, label, value, onChange, hasError, hasSuccess, helper }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full px-3 py-2 pr-10 text-sm border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
            hasError
              ? 'border-[#e3282a] focus:ring-red-400'
              : hasSuccess
              ? 'border-[#1FA94B] focus:ring-emerald-300'
              : 'border-gray-300 focus:ring-red-400'
          }`}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 bg-transparent border-0 cursor-pointer p-1"
        >
          {visible ? <FiEyeOff size={16} /> : <FiEye size={16} />}
        </button>
      </div>
      {helper}
    </div>
  );
};

const PasswordChangeCard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [oldPassword, setOld] = useState('');
  const [newPassword, setNew] = useState('');
  const [confirmPassword, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const criteriaState = useMemo(
    () =>
      PASSWORD_CRITERIA.map((c) => ({
        ...c,
        passed: c.test(newPassword),
      })),
    [newPassword]
  );
  const allCriteriaPassed = criteriaState.every((c) => c.passed);
  const passwordsMatch =
    newPassword.length > 0 && newPassword === confirmPassword;
  const canSubmit =
    !!oldPassword && allCriteriaPassed && passwordsMatch && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    const result = await dispatch(
      updatePassword({ old_password: oldPassword, new_password: newPassword })
    );
    setSubmitting(false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((result as any)?.meta?.requestStatus === 'fulfilled') {
      setOld('');
      setNew('');
      setConfirm('');
      dispatch(setNotification('Пароль изменён. Войдите заново.'));
    }
  };

  const handleCancel = () => {
    setOld('');
    setNew('');
    setConfirm('');
  };

  return (
    <div className="bg-white rounded-xl shadow-sm px-6 py-5">
      <h2 className="text-lg font-semibold text-[#2d2c2c]">Безопасность</h2>
      <p className="text-sm text-gray-500 mt-0.5 mb-5">
        Смена пароля для входа в систему
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <div className="flex flex-col gap-4 max-w-[480px]">
          <PasswordInput
            id="old_password"
            label="Текущий пароль"
            value={oldPassword}
            onChange={setOld}
          />
          <PasswordInput
            id="new_password"
            label="Новый пароль"
            value={newPassword}
            onChange={setNew}
            hasSuccess={newPassword.length > 0 && allCriteriaPassed}
          />
          <PasswordInput
            id="confirm_password"
            label="Повторите новый пароль"
            value={confirmPassword}
            onChange={setConfirm}
            hasError={confirmPassword.length > 0 && !passwordsMatch}
            hasSuccess={passwordsMatch}
            helper={
              confirmPassword.length > 0 ? (
                <span
                  className={`text-sm ${
                    passwordsMatch ? 'text-[#1FA94B]' : 'text-[#e3282a]'
                  }`}
                >
                  {passwordsMatch ? 'Пароли совпадают' : 'Пароли не совпадают'}
                </span>
              ) : null
            }
          />
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-5 self-start">
          <div className="flex flex-col gap-3">
            {criteriaState.map((c) => (
              <div key={c.label} className="flex items-start gap-2.5">
                <div
                  className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                    c.passed ? 'bg-[#1FA94B]' : 'bg-gray-300'
                  }`}
                >
                  <FiCheck size={12} color="white" />
                </div>
                <div>
                  <div className="text-sm font-medium text-[#2d2c2c]">
                    {c.label}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 leading-snug">
                    {c.hint}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-6">
        <button
          type="button"
          onClick={handleCancel}
          className="px-4 py-2 text-sm rounded-lg bg-gray-100 text-gray-700 border-0 cursor-pointer hover:bg-gray-200"
        >
          Отменить
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="px-4 py-2 text-sm rounded-lg bg-[#e3282a] text-white border-0 cursor-pointer hover:bg-[#c91f21] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Сменить пароль
        </button>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------
// Email block — двухшаговая смена через код на текущую почту.
// ----------------------------------------------------------------------

// Универсальная модалка двухшаговой смены реквизита (почта или логин).
// Логика обоих flow идентична: ввод нового значения → код на ТЕКУЩУЮ
// почту → подтверждение. Чтобы не дублировать ~140 строк JSX,
// модалка принимает API-методы и тексты параметрами.
interface CredentialChangeModalProps {
  // Что меняем — отображается в заголовке и метках полей.
  titleEnter: string;
  titleCode: string;
  fieldLabel: string;
  fieldPlaceholder: string;
  fieldType?: 'email' | 'text';
  // Куда придёт код подтверждения (текущая почта). Показываем
  // пользователю явно — пусть видит, на какой адрес ждать письма.
  notifyEmail: string;
  // Промисы из auth.service.ts. Намеренно не зашиты внутрь, чтобы
  // модалка не знала про конкретный API и легко переиспользовалась.
  request: (value: string) => Promise<unknown>;
  confirm: (code: string) => Promise<unknown>;
  // Сообщение после успешного confirm — fly-to уведомление.
  successText: string;
  // Какие ключи ошибок ловить в .response.data (бэк отдаёт разные:
  // у email это `new_email`, у username — `new_username`).
  apiErrorKeys: string[];
  onClose: () => void;
  onChanged: () => void;
}

const CredentialChangeModal: React.FC<CredentialChangeModalProps> = ({
  titleEnter,
  titleCode,
  fieldLabel,
  fieldPlaceholder,
  fieldType = 'text',
  notifyEmail,
  request,
  confirm,
  successText,
  apiErrorKeys,
  onClose,
  onChanged,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  // step "enter" — пользователь вводит новое значение;
  // step "code"  — пользователь вводит код из письма на notifyEmail.
  const [step, setStep] = useState<'enter' | 'code'>('enter');
  const [value, setValue] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  // Достаём текст ошибки из ответа бэка. DRF отдаёт ошибки по полям
  // ({ new_email: ["..."] }) либо общим ключом detail/non_field_errors.
  // Имя поля зависит от того, что меняем — поэтому ключи приходят пропсом.
  const extractError = (e: unknown, fallback: string): string => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const detail = (e as any)?.response?.data;
    if (detail) {
      for (const k of ['code', ...apiErrorKeys, 'detail', 'non_field_errors']) {
        const v = detail[k];
        if (Array.isArray(v) && v[0]) return v[0];
        if (typeof v === 'string' && v) return v;
      }
    }
    return fallback;
  };

  const requestCode = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await request(value);
      setStep('code');
      dispatch(setNotification(`Код подтверждения отправлен на ${notifyEmail}.`));
    } catch (e) {
      dispatch(
        setErrorNotification(
          extractError(e, 'Не удалось отправить код. Попробуйте ещё раз.')
        )
      );
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await confirm(code);
      dispatch(setNotification(successText));
      onChanged();
    } catch (e) {
      // 400 от бэка: «Код недействителен или истёк.» или
      // «Эта почта/логин уже занят».
      dispatch(
        setErrorNotification(
          extractError(e, 'Неверный код. Проверьте письмо и попробуйте снова.')
        )
      );
    } finally {
      setBusy(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-[480px] p-6 relative">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-700 bg-transparent border-0 cursor-pointer"
        >
          <FiX size={18} />
        </button>
        {step === 'enter' ? (
          <>
            <h3 className="text-lg font-semibold text-[#2d2c2c] mb-1">
              {titleEnter}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Код подтверждения придёт на текущий адрес{' '}
              <span className="font-medium text-[#2d2c2c]">
                {notifyEmail || 'почты нет'}
              </span>
              . Если у вас нет к нему доступа — обратитесь к администратору.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {fieldLabel}
            </label>
            <input
              type={fieldType}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={fieldPlaceholder}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400"
            />
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm rounded-lg bg-gray-100 text-gray-700 border-0 cursor-pointer hover:bg-gray-200"
              >
                Отменить
              </button>
              <button
                onClick={requestCode}
                disabled={!value || busy}
                className="px-4 py-2 text-sm rounded-lg bg-[#e3282a] text-white border-0 cursor-pointer hover:bg-[#c91f21] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Отправить код
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 className="text-lg font-semibold text-[#2d2c2c] mb-1">
              {titleCode}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Письмо с кодом отправлено на{' '}
              <span className="font-medium text-[#2d2c2c]">{notifyEmail}</span>
              . Код действителен 15 минут.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Код подтверждения
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.trim())}
              placeholder="00000000-0000-0000-0000-000000000000"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 font-mono"
            />
            <div className="flex justify-between items-center mt-5">
              <button
                onClick={() => setStep('enter')}
                className="text-sm text-gray-500 hover:text-[#2d2c2c] bg-transparent border-0 cursor-pointer underline"
              >
                Отправить код ещё раз
              </button>
              <div className="flex gap-2">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm rounded-lg bg-gray-100 text-gray-700 border-0 cursor-pointer hover:bg-gray-200"
                >
                  Отменить
                </button>
                <button
                  onClick={submitCode}
                  disabled={!code || busy}
                  className="px-4 py-2 text-sm rounded-lg bg-[#e3282a] text-white border-0 cursor-pointer hover:bg-[#c91f21] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Подтвердить
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

// Общий шаблон карточки «Поле + кнопка смены». Используется для
// почты и логина — оба flow двухшаговые с кодом на текущую почту.
const CredentialCard: React.FC<{
  title: string;
  description: string;
  fieldLabel: string;
  fieldValue: string;
  buttonLabel: string;
  modalProps: Omit<CredentialChangeModalProps, 'onClose' | 'onChanged'>;
  onChanged: () => void;
}> = ({
  title,
  description,
  fieldLabel,
  fieldValue,
  buttonLabel,
  modalProps,
  onChanged,
}) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="bg-white rounded-xl shadow-sm px-6 py-5">
        <h2 className="text-lg font-semibold text-[#2d2c2c]">{title}</h2>
        <p className="text-sm text-gray-500 mt-0.5 mb-5">{description}</p>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">
              {fieldLabel}
            </div>
            <div className="text-sm text-[#2d2c2c] font-medium break-all">
              {fieldValue || '—'}
            </div>
          </div>
          <button
            onClick={() => setOpen(true)}
            className="px-4 py-2 text-sm rounded-lg bg-gray-100 text-gray-700 border-0 cursor-pointer hover:bg-gray-200"
          >
            {buttonLabel}
          </button>
        </div>
      </div>
      {open && (
        <CredentialChangeModal
          {...modalProps}
          onClose={() => setOpen(false)}
          onChanged={() => {
            setOpen(false);
            onChanged();
          }}
        />
      )}
    </>
  );
};

// ----------------------------------------------------------------------
// Page
// ----------------------------------------------------------------------

const SecuritySettings: React.FC = () => {
  const me = useSelector(selectUser);
  const dispatch = useDispatch<AppDispatch>();

  // После смены email/логина бэк инвалидирует все токены. Зовём getMe —
  // axios-интерсептор поймает 401 и уведёт юзера на /login.
  const onCredentialChanged = () => {
    dispatch(getMe());
  };

  return (
    <div className="space-y-6">
      <PasswordChangeCard />
      {me && (
        <CredentialCard
          title="Почта"
          description="На текущую почту приходят системные уведомления и коды подтверждения. Сменить можно только с подтверждением через старый адрес."
          fieldLabel="Текущая почта"
          fieldValue={me.email}
          buttonLabel="Изменить почту"
          onChanged={onCredentialChanged}
          modalProps={{
            titleEnter: 'Изменить почту',
            titleCode: 'Введите код из письма',
            fieldLabel: 'Новая почта',
            fieldPlaceholder: 'example@mail.ru',
            fieldType: 'email',
            notifyEmail: me.email,
            request: (new_email) => authApi.requestEmailChange({ new_email }),
            confirm: (code) => authApi.confirmEmailChange({ code }),
            successText: 'Почта изменена. Войдите заново.',
            apiErrorKeys: ['new_email'],
          }}
        />
      )}
      {me && (
        <CredentialCard
          title="Логин"
          description="Логин используется для входа в систему. Сменить можно только с подтверждением кодом на текущую почту."
          fieldLabel="Текущий логин"
          fieldValue={me.username}
          buttonLabel="Изменить логин"
          onChanged={onCredentialChanged}
          modalProps={{
            titleEnter: 'Изменить логин',
            titleCode: 'Введите код из письма',
            fieldLabel: 'Новый логин',
            fieldPlaceholder: 'new_login',
            fieldType: 'text',
            notifyEmail: me.email,
            request: (new_username) =>
              authApi.requestUsernameChange({ new_username }),
            confirm: (code) => authApi.confirmUsernameChange({ code }),
            successText: 'Логин изменён. Войдите заново.',
            apiErrorKeys: ['new_username'],
          }}
        />
      )}
    </div>
  );
};

export default SecuritySettings;
